import React, { createRef, useEffect, useRef } from 'react'

import Script from 'next/script'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classNames from 'classnames'

import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import KeyMessageBanner, {
  ImageAlignmentVariants,
} from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import { ProfileImageType } from '~/components/atoms/ProfileImage/ProfileImage'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import MagazineArticleExpert from '~/components/blocks/MagazineArticleExpert/MagazineArticleExpert'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import RelatedArtworks from '~/components/blocks/RelatedArtworks/RelatedArtworks'
import type { StickySectionHeaderProps } from '~/components/blocks/StickySectionHeader/StickySectionHeader'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import type { PageData } from '~/pages/[lang]/edition/[category]/[articleTaxonomy]/[slug]'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import generateSkipLinks from '~/services/generateSkipLinks'
import { useMediaQuery } from '~/services/useMediaQuery'

import styles from '~/styles/pages.module.css'
import RecordTypes = indexes.RecordTypes
import { useRouter } from 'next/router'

import { usePostHog } from 'posthog-js/react'

import Fixture from '~/components/atoms/Fixture/Fixture'
import SignUpForPremiumBanner from '~/components/blocks/SignUpForPremiumBanner/SignUpForPremiumBanner'
import useMabPostHog, {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import usePremiumContent from '~/hooks/usePremiumContent'
import { useLatestArtworks } from '~/services/query'

import { ADVERT_CONFIG } from './advertConfig'
import PodcastVideoArticle from './PodcastVideoArticle'
import StandardArticle from './StandardArticle'

const ArticleContainer = (props: PageData) => {
  const {
    modules,
    relatedArtworks,
    body,
    pbModules,
    leadImage,
    relatedArticles,
    relatedParentArticles,
    articleHeader,
    relatedArtist,
    relatedArtistWorks,
    relatedGenre,
    relatedGenreWorks,
    banner,
    numberedList,
    expert,
    defaultExpert,
    breadcrumb,
    stickyOverrideCta,
    relatedArtists,
    displaySideMenu,
    video,
    podcast,
    keySummary,
    advert,
    relatedCollections,
    isPremium,
    featuredReport,
    secondaryCategories,
  } = props

  const router = useRouter()
  const posthog = usePostHog()
  const t = useTranslations('articleContainer')

  const loginGateFindIndex = modules?.findIndex(
    item => item._type === 'articleLoginGate'
  )
  const loginGatePosition =
    loginGateFindIndex !== -1 ? loginGateFindIndex - 1 : 2

  useEffect(() => {
    if (router?.query?.stickyVariant) {
      posthog.featureFlags.override({ ab_control_recordpricescta: 'sticky' })
    }
  }, [router])

  let { valuationCTAPosition } = props

  if (valuationCTAPosition === null || !valuationCTAPosition)
    valuationCTAPosition = ADVERT_CONFIG.DEFAULT_VALUATION_CTA_POSITION

  const { mainHeaderVisible } = useInPageNavContext()
  const refs = useRef(modules?.map(() => createRef<HTMLDivElement>()))

  const { showPremiumPrompt } = usePremiumContent()
  const hideToNonePremium = isPremium && showPremiumPrompt

  const isMobile = useMediaQuery(
    `(max-width: ${ADVERT_CONFIG.BREAKPOINTS.MOBILE}px)`
  )
  const isSmallerScreenWidth = useMediaQuery(
    `(max-width: ${ADVERT_CONFIG.BREAKPOINTS.SMALLER_SCREEN}px)`
  )
  const stickyLinkBar: StickySectionHeaderProps = (() => {
    switch (true) {
      case !!relatedGenre:
        return {
          ...relatedGenre,
          totalWorks: relatedGenreWorks,
          stickyOverrideCta: stickyOverrideCta,
          category:
            props.articleHeader.customTaxonomy === 'guides'
              ? 'guides'
              : props.articleHeader.articleTypeSlug,
          customTaxonomy: props?.articleHeader?.customTaxonomy,
          title: props.articleHeader.title,
        }
      case !!relatedArtist:
        return {
          name: relatedArtist?.name,
          type: relatedArtist?.type,
          slug: relatedArtist?.slug,
          stickyOverrideCta: stickyOverrideCta,
          totalWorks: relatedArtistWorks,
          id: relatedArtist?.id,
          image: relatedArtist?.image?.asset,
          customTaxonomy: props?.articleHeader?.customTaxonomy,
          category:
            props?.articleHeader?.customTaxonomy === 'guides'
              ? 'guides'
              : props?.articleHeader?.articleTypeSlug,
          title: props.articleHeader?.title,
        }
      default:
        return {
          name: props.articleHeader.title,
          type: 'Article',
          slug: '',
          category:
            props?.articleHeader?.customTaxonomy === 'guides'
              ? 'guides'
              : props?.articleHeader?.articleTypeSlug,
        }
    }
  })()

  const relatedArtworksEvents = {
    artwork: {
      method: SearchAnalystMethods.clickedObjectIDs,
      name: SearchAnalystEventNames.articleRelatedArtworkClicked,
    },
    artist: {
      method: SearchAnalystMethods.clickedFilters,
      name: SearchAnalystEventNames.articleRelatedArtworkArtistClicked,
    },
  }

  const articleExpert = expert
    ? expert
    : relatedArtist
      ? relatedArtist?.expert
      : defaultExpert

  const skipLinks = generateSkipLinks(modules)

  // todo - turn on once live
  // const isMenuVisibleSet = displaySideMenu === true
  const hideSideMenu = displaySideMenu === false

  // Determine whether an article is a video or podcast. The primary signal is
  // the (legacy) articleTypeTitle; we also fall back to the article's secondary
  // categories, matching either their name or slug, since podcast/video
  // articles often carry the type there rather than in the primary category.
  const matchesArticleCategory = (keywords: string[]) => {
    const articleTypeTitle =
      articleHeader?.articleTypeTitle?.toLowerCase() || ''
    if (keywords.includes(articleTypeTitle)) return true

    return (secondaryCategories || []).some(
      category =>
        keywords.includes(category?.name?.toLowerCase() || '') ||
        keywords.includes(category?.slug?.current?.toLowerCase() || '')
    )
  }

  const matchesVideoCategory = matchesArticleCategory(['video', 'videos'])
  const matchesPodcastCategory = matchesArticleCategory(['podcasts', 'podcast'])

  // A true video/podcast page is just the media plus a short blurb. A standard
  // article can also carry a video and be tagged with a video category, but it
  // has real body content. Treat anything beyond a single leadParagraph as
  // article content, so those render in the standard article layout.
  const hasArticleBodyContent = (modules || []).some(
    module => module._type !== 'leadParagraph'
  )

  const isPodcastOrVideo =
    (matchesVideoCategory || matchesPodcastCategory) && !hasArticleBodyContent

  // The categories can match both (e.g. a "Podcast" series published as a
  // video), so prefer the media the article actually has: a video ref wins,
  // then a podcast ref, falling back to the category match otherwise.
  const resolveIsVideo = () => {
    if (video) return true
    if (podcast) return false
    return matchesVideoCategory
  }
  const isVideo = isPodcastOrVideo && resolveIsVideo()
  const isPodcast = isPodcastOrVideo && !isVideo

  const showDesktopStickyContent = !isMobile && !isSmallerScreenWidth

  let pageBreadCrumbs = breadcrumb

  let relatedArtworksData = relatedArtworks
  const { data, isLoading, insightsQueryID } = useLatestArtworks()
  if (!relatedArtworksData) {
    relatedArtworksData = isLoading ? null : data
  }

  const renderExpertBlock = (
    location: 'Header' | 'Footer',
    useStickyBehaviour = false
  ) => {
    return (
      <Container gutters={gutterTypes.always}>
        <MagazineArticleExpert
          stickyLinkBar={stickyLinkBar}
          useStickyBehaviour={useStickyBehaviour}
          noMarginBottom={!!(isMobile && (advert || isPodcast || isVideo))}
          location={location}
          artist={
            relatedArtist
              ? {
                  name: relatedArtist?.name,
                  slug: relatedArtist?.slug,
                }
              : undefined
          }
        />
      </Container>
    )
  }

  const updateBreadcrumbForVideo = (
    breadcrumb: any,
    title: string,
    url: string,
    item: string
  ) => {
    const updatedBreadcrumb = [...breadcrumb]

    updatedBreadcrumb.splice(1, 0, {
      title: title,
      url: url,
      item: item,
    })

    return updatedBreadcrumb
  }

  const { pushEvent } = useMabPostHog()
  const handleValuationsBannerAnalytics = () => {
    pushEvent('Article Instant Valuation Banner Clicked', {
      webProduct: PushWebProduct.Articles,
      interactionType: PushInteractionType.Click,
      interactionLocation: PushInteractionLocation.ArticleContent,
    })
  }

  if (isVideo) {
    pageBreadCrumbs = updateBreadcrumbForVideo(
      breadcrumb,
      t('videos'),
      '/articles/videos',
      'videos'
    )
  }
  if (isPodcast) {
    pageBreadCrumbs = updateBreadcrumbForVideo(
      breadcrumb,
      t('podcasts'),
      '/articles/podcasts',
      'podcasts'
    )
  }

  const isValuationsOldBannerAd =
    advert?.link === 'https://www.myartbroker.com/valuations'

  return (
    <>
      <div id="fb-root"></div>
      <Script
        src="https://connect.facebook.net/en_GB/sdk.js#xfbml=1&version=v12.0"
        nonce="LJwTB7nZ"
      />

      <div className="article" id="Top">
        {hideToNonePremium && <SignUpForPremiumBanner />}
        {pageBreadCrumbs && (
          <Breadcrumb
            transparent={!isPodcastOrVideo && true}
            list={pageBreadCrumbs}
            negativeMargin
          />
        )}

        {isPodcastOrVideo && (
          <PodcastVideoArticle
            isVideo={isVideo}
            isPodcast={isPodcast}
            video={video}
            podcast={podcast}
            articleHeader={articleHeader}
            keySummary={keySummary}
            renderExpertBlock={renderExpertBlock}
          />
        )}

        {!isPodcastOrVideo && (
          <StandardArticle
            articleHeader={articleHeader}
            video={video}
            leadImage={leadImage}
            modules={modules}
            keySummary={keySummary}
            numberedList={numberedList}
            body={body}
            advert={advert}
            relatedArticles={relatedArticles}
            relatedArtist={relatedArtist}
            featuredReport={featuredReport}
            refs={refs}
            mainHeaderVisible={mainHeaderVisible}
            hideSideMenu={hideSideMenu}
            skipLinks={skipLinks}
            hideToNonePremium={!!hideToNonePremium}
            isMobile={isMobile}
            isSmallerScreenWidth={isSmallerScreenWidth}
            showDesktopStickyContent={showDesktopStickyContent}
            isValuationsOldBannerAd={isValuationsOldBannerAd}
            loginGatePosition={loginGatePosition}
            valuationCTAPosition={valuationCTAPosition}
            onValuationClick={handleValuationsBannerAnalytics}
            renderExpertBlock={renderExpertBlock}
          />
        )}

        {pbModules && (
          <div className={styles.body}>
            {pbModules.map((item, x) => resolveBlock(item, x, 'magazine'))}
          </div>
        )}

        {relatedArtworksData && (
          <RelatedArtworks
            overrideData={relatedArtworksData}
            relatedArtist={relatedArtist}
            showAllLink={true}
            searchInsightsEvents={relatedArtworksEvents}
            title={t('buyAndSellArtworks')}
          />
        )}

        {relatedArticles && relatedArticles.length > 0 && (
          <div
            className={classNames({
              [styles.videoRelatedArticlesContainerFooter]: isPodcastOrVideo,
            })}
          >
            <RelatedArticles
              articles={relatedArticles}
              theme={ComponentTheme.secondary}
              searchInsightsEvent={{
                method: SearchAnalystMethods.clickedObjectIDs,
                name: SearchAnalystEventNames.articleRelatedArticleClicked,
              }}
            />
          </div>
        )}

        {!relatedArticles && relatedParentArticles?.length > 0 && (
          <RelatedArticles
            articles={relatedParentArticles}
            theme={ComponentTheme.secondary}
            searchInsightsEvent={{
              method: SearchAnalystMethods.clickedObjectIDs,
              name: SearchAnalystEventNames.articleRelatedArticleClicked,
            }}
          />
        )}

        {relatedCollections && relatedCollections.length > 0 && (
          <VerticalPadder noPaddingBottom={false}>
            <ArtistProfileList
              artists={relatedCollections}
              totalArtists={relatedCollections.length}
              type={ProfileImageType.Artwork}
              allBtnHandler={() => false}
              hideViewAll={true}
              title={t('notableCollections')}
              showNumberOfWorks={true}
              searchInsightsEvent={{
                method: SearchAnalystMethods.clickedFilters,
                name: SearchAnalystEventNames.collectionRelatedCollectionClicked,
              }}
              recordType={RecordTypes.COLLECTION}
            />
          </VerticalPadder>
        )}

        {isPodcastOrVideo && relatedArtists && relatedArtists?.length > 0 && (
          <Fixture verticalPadding={true}>
            <ArtistProfileList
              artists={relatedArtists as any}
              title={t('relatedArtists')}
              hideViewAll={true}
              totalArtists={0}
              totalArtistsTitle={t('allCollections')}
              allBtnHandler={() => false}
              recordType={RecordTypes.ARTIST}
              searchInsightsEvent={{
                method: SearchAnalystMethods.clickedFilters,
                name: SearchAnalystEventNames.artistRelatedArtistClicked,
              }}
            />
          </Fixture>
        )}

        {banner && banner?.images && (
          <KeyMessageBanner
            {...banner}
            imageAlign={ImageAlignmentVariants.full}
          />
        )}
      </div>
    </>
  )
}

export default ArticleContainer
